import {
  NetIncomeSummary,
  NetIncomeSummarySource
} from "types/landlord/reports";
import { Fragment } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import moment from "moment";
import { formatToCurrency } from "../../../../../../utils/currency";
import { chartColors } from "../constants";

interface VerifiedNetEarningsTableExpanderProps {
  rowData: NetIncomeSummary;
}

const columnHelper = createColumnHelper<NetIncomeSummarySource>();

const columns = [
  columnHelper.accessor("pay_date", {
    cell: ({ getValue }) => {
      return (
        <>
          {/*circle*/}
          <div className="absolute top-1/2 transform -translate-y-1/2 left-14 size-1.5 bg-content-100 rounded-full" />
          {/*dashed line*/}
          <div className="absolute top-1/2 transform -translate-y-1/2 left-8 border-dashed border-content-100 border-t w-6" />
          <p className="xl:ml-14 ml-[65px]">
            {moment(getValue()).format("MMMM D, YYYY")}
          </p>
        </>
      );
    }
  }),
  columnHelper.accessor("description", {
    cell: ({ getValue }) => {
      return (
        <div className="flex items-center">
          <div
            className="size-2 min-w-2 rounded-full"
            style={{
              backgroundColor: chartColors[0]
            }}
          />
          <p className="ml-2">{getValue()}</p>
        </div>
      );
    }
  }),
  columnHelper.accessor("net_pay", {
    cell: ({ getValue }) => {
      const data = getValue();
      return data?.cents ? formatToCurrency(data) : "-";
    }
  })
];

const ROW_HEIGHT = 64;

export const VerifiedNetEarningsTableExpander = ({
  rowData
}: VerifiedNetEarningsTableExpanderProps) => {
  const sources = rowData.sources;
  const table = useReactTable({
    data: sources,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const rowModel = table.getRowModel();

  const lineHeight = ROW_HEIGHT * 0.5 + ROW_HEIGHT * (sources.length - 1);

  return (
    <Fragment>
      {/*dashed line*/}
      <div
        className="absolute border-l border-content-100 left-8 border-dashed"
        style={{ height: `${lineHeight}px` }}
      />
      {rowModel.rows.map((row, row_idx: number) => {
        return (
          <Fragment key={`row_${row_idx}`}>
            <tr
              className="text-content-100 bg-background-900"
              style={{
                height: `${ROW_HEIGHT}px`
              }}
            >
              {row.getVisibleCells().map((cell, idx) => {
                const cellContent = flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                );
                return (
                  <td key={`cell_${idx}`} className="relative">
                    <div className="animate-appear flex h-12 max-h-12 items-center xl:px-4 px-1 py-0">
                      <div className="m-0 text-left text-sm font-normal text-content-500">
                        {cellContent}
                      </div>
                    </div>
                  </td>
                );
              })}
              {/* This is needed for the expanded row to be rendered correctly, otherwise background will cut */}
              <td />
            </tr>
          </Fragment>
        );
      })}
    </Fragment>
  );
};
